import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import {
  applyHubspotImport,
  getHubspotImportOpportunity,
  getHubspotImportRows,
  getHubspotImportStatus,
  planHubspotImport,
} from '../../../requests/opsHubspot';
import {
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../ui';

const when = (iso?: string | Date | null) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const money = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const FLAG_HELP: Record<string, string> = {
  'close-date-past': 'open in Salesforce with a close date already behind it',
  'owner-not-in-hubspot':
    'the seller has no HubSpot seat; the deal imports unassigned',
  'owner-archived':
    'the seller has no HubSpot seat; the deal takes the archive owner, the seller stays in sf_owner_email',
  'open-deal-archived-owner':
    'an OPEN deal on the archive owner — reassign it to a seller after import',
  'company-will-be-created':
    'no HubSpot company matched the account by domain; one is created',
  'stage-folded': 'Hold or Qualification, folded into Qualify',
  'unmapped-stage': 'a stage name not on the pipeline',
  'no-amount': 'no amount in Salesforce',
  'no-account': 'no account on the opportunity',
};

/**
 * The Salesforce → HubSpot import: plan from the mirror, read it, apply.
 * Everything here is dry-run until HUBSPOT_IMPORT=live and the write token
 * are on the box; the Apply button says so instead of pretending.
 */
export const ImportCard: React.FC = () => {
  const queryClient = useQueryClient();
  const [flag, setFlag] = useState<string | null>(null);
  /** Which rows the table below shows: a planned flag, or what apply did. */
  const [view, setView] = useState<'created' | 'failed' | null>(null);
  /** Rows written per apply click. Start small on the first live run. */
  const [applyLimit, setApplyLimit] = useState<number>(500);
  const { data: st, isPending } = useQuery({
    queryKey: ['opsHubspot', 'import', 'status'],
    queryFn: getHubspotImportStatus,
  });
  const { data: rows } = useQuery({
    queryKey: ['opsHubspot', 'import', 'rows', flag, view],
    queryFn: () =>
      view
        ? getHubspotImportRows({ status: view, limit: 500 })
        : getHubspotImportRows({ flag: flag ?? undefined, limit: 100 }),
    enabled: Boolean(flag || view),
  });
  const pickFlag = (k: string) => {
    setView(null);
    setFlag(flag === k ? null : k);
  };
  const pickView = (v: 'created' | 'failed') => {
    setFlag(null);
    setView(view === v ? null : v);
  };
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['opsHubspot', 'import'] });
  const plan = useMutation({
    mutationFn: planHubspotImport,
    onSuccess: (r) => {
      showToast(
        r.ok === false
          ? r.error ?? r.reason ?? 'Plan failed'
          : `Planned: ${r.scope?.open ?? 0} open · ${
              r.scope?.closedWon ?? 0
            } won · ${r.scope?.closedLost ?? 0} lost`,
        r.ok === false ? 'error' : 'success',
      );
      refresh();
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });
  const apply = useMutation({
    mutationFn: () => applyHubspotImport(applyLimit),
    onSuccess: (r) => {
      showToast(
        r.ran
          ? r.ok === false
            ? r.error ?? 'Apply failed'
            : `${r.created ?? 0} created · ${r.linked ?? 0} linked · ${
                r.failed ?? 0
              } failed`
          : `${r.reason} — would create ${r.wouldCreate ?? 0} deals`,
        r.ran && r.ok !== false ? 'success' : 'error',
      );
      refresh();
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });
  const busy = plan.isPending || apply.isPending;

  if (isPending || !st) return <LoadingSpinner />;
  const flags = Object.entries(st.lastPlan?.flags ?? {}).sort(
    (a, b) => b[1] - a[1],
  );
  const totalPlanned = st.stages.reduce((n, s) => n + s.n, 0);

  return (
    <Card
      title="Salesforce → HubSpot import"
      className="mt-6"
      right={
        <div className="flex gap-2">
          <button
            className={btnSecondary}
            disabled={busy}
            onClick={() => plan.mutate()}
          >
            Plan from the mirror
          </button>
          <label
            className="flex items-center gap-1 text-xs text-gray-600"
            title="How many planned rows this click writes. Use 5 for the first live run, then raise it."
          >
            rows
            <input
              type="number"
              min={1}
              max={5000}
              value={applyLimit}
              disabled={busy}
              onChange={(e) =>
                setApplyLimit(
                  Math.max(1, Math.min(5000, Number(e.target.value) || 1)),
                )
              }
              className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm"
            />
          </label>
          <button
            className={btnPrimary}
            disabled={busy || totalPlanned === 0}
            onClick={() => apply.mutate()}
            title={
              st.mode === 'live' && st.writeConfigured
                ? 'Writes the planned rows to HubSpot'
                : 'Dry-run: reports what would be written'
            }
          >
            {st.mode === 'live' && st.writeConfigured
              ? 'Apply to HubSpot'
              : 'Apply (dry-run)'}
          </button>
        </div>
      }
    >
      <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2 text-sm">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
            Mode
          </div>
          <Pill
            value={st.mode}
            tone={st.mode === 'live' ? 'good' : 'neutral'}
          />
          <span className="ml-2 text-xs text-gray-bold">
            write token {st.writeConfigured ? 'set' : 'not set'} · read token{' '}
            {st.readConfigured ? 'set' : 'not set'}
          </span>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
            Pipeline
          </div>
          <span className="text-gray-five">{st.pipelineLabel}</span>
          <span className="ml-2 text-xs text-gray-bold">
            {st.pipeline
              ? `exists (${st.pipeline.id})`
              : 'created on first apply'}
          </span>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
            Closed history from
          </div>
          <span className="text-gray-five">{st.since}</span>
          <span className="ml-2 text-xs text-gray-bold">
            open opportunities always
          </span>
        </div>
        {(
          [
            ['Planned', st.counts.planned],
            ['Created', st.counts.created],
            ['Failed', st.counts.failed],
          ] as const
        ).map(([label, n]) => {
          const v =
            label === 'Created'
              ? 'created'
              : label === 'Failed'
              ? 'failed'
              : null;
          return (
            <div
              key={label}
              className={
                v && n > 0
                  ? `cursor-pointer rounded-lg px-2 -mx-2 ${
                      view === v ? 'bg-blue-50' : 'hover:bg-grey-50'
                    }`
                  : ''
              }
              title={v && n > 0 ? `Show the ${label.toLowerCase()} rows` : ''}
              onClick={() => v && n > 0 && pickView(v)}
            >
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                {label}
              </div>
              <div className="text-lg font-semibold text-gray-five">
                {n.toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      {totalPlanned === 0 ? (
        <div className="mt-4">
          <EmptyState
            title="No plan yet"
            hint="Plan from the mirror reads every open opportunity and the closed history, resolves owners and companies against HubSpot, and writes nothing there."
          />
        </div>
      ) : (
        <div className="mt-4 grid gap-6 md:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-semibold text-gray-three">
              By stage
            </div>
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Stage</th>
                  <th className={`${thClass} text-right`}>Deals</th>
                  <th className={`${thClass} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {st.stages.map((s) => (
                  <tr key={s.key}>
                    <td className={tdClass}>{s.label}</td>
                    <td className={`${tdClass} text-right`}>
                      {s.n.toLocaleString()}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {money(s.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 text-xs text-gray-bold">
              Companies: {st.companies.link ?? 0} link to an existing HubSpot
              company · {st.companies.create ?? 0} created. Contacts:{' '}
              {st.contacts.link ?? 0} link · {st.contacts.create ?? 0} created ·{' '}
              {st.contacts.none ?? 0} none named.
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold text-gray-three">
              Read before applying
              {st.lastPlan && (
                <span className="ml-2 font-normal text-gray-bold">
                  planned {when(st.lastPlan.at)} by {st.lastPlan.by}
                  {st.lastPlan.hubspotRead
                    ? ''
                    : ' · HubSpot not read (no token)'}
                </span>
              )}
            </div>
            {st.lastPlan?.owners?.missing?.length ? (
              <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                No HubSpot seat for: {st.lastPlan.owners.missing.join(', ')}.{' '}
                {st.lastPlan.owners.archive?.id
                  ? `Their deals import owned by ${st.lastPlan.owners.archive.email}, with the Salesforce owner kept on the deal.`
                  : 'Their deals import unassigned, with the Salesforce owner kept on the deal.'}
              </div>
            ) : null}
            {flags.length === 0 ? (
              <div className="text-xs text-gray-bold">Nothing flagged.</div>
            ) : (
              <table className={tableClass}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Flag</th>
                    <th className={`${thClass} text-right`}>Deals</th>
                  </tr>
                </thead>
                <tbody>
                  {flags.map(([k, n]) => (
                    <tr
                      key={k}
                      className={`cursor-pointer ${
                        flag === k ? 'bg-blue-50' : 'hover:bg-grey-50'
                      }`}
                      onClick={() => pickFlag(k)}
                    >
                      <td className={tdClass}>
                        <div className="font-medium text-gray-five">{k}</div>
                        <div className="text-xs text-gray-bold">
                          {FLAG_HELP[k] ?? ''}
                        </div>
                      </td>
                      <td className={`${tdClass} text-right`}>
                        {n.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {(flag || view) && (
        <div className="mt-4">
          <div className="mb-1 text-xs font-semibold text-gray-three">
            {view
              ? `${view === 'created' ? 'Created in HubSpot' : 'Failed'} — ${
                  rows?.length ?? 0
                } shown`
              : `${flag} — first ${rows?.length ?? 0}`}
            {view === 'created' && !st.dealUrlBase ? (
              <span className="ml-2 font-normal text-gray-bold">
                (set HUBSPOT_DEAL_URL_BASE to link each row to its HubSpot deal)
              </span>
            ) : null}
          </div>
          {!rows ? (
            <LoadingSpinner />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Opportunity</th>
                  <th className={thClass}>Stage</th>
                  <th className={`${thClass} text-right`}>Amount</th>
                  <th className={thClass}>Close</th>
                  <th className={thClass}>Owner</th>
                  <th className={thClass}>
                    {view === 'created'
                      ? 'HubSpot deal'
                      : view === 'failed'
                      ? 'Error'
                      : 'Flags'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.sfId}>
                    <td className={tdClass}>
                      <a
                        className="text-blue-primary hover:underline"
                        href={`?opp=${r.sfId}`}
                      >
                        {r.properties.dealname}
                      </a>
                    </td>
                    <td className={tdClass}>
                      {r.properties.sf_stage_name ?? r.stageKey}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {r.amount != null ? money(r.amount) : '—'}
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {r.closeDate ?? '—'}
                    </td>
                    <td className={`${tdClass} text-xs`}>
                      {r.ownerEmail ?? '—'}
                    </td>
                    <td className={`${tdClass} text-xs text-gray-bold`}>
                      {view === 'created' ? (
                        r.hsDealId ? (
                          st.dealUrlBase ? (
                            <a
                              className="text-blue-primary hover:underline"
                              href={`${st.dealUrlBase}/${r.hsDealId}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {r.hsDealId} ↗
                            </a>
                          ) : (
                            r.hsDealId
                          )
                        ) : (
                          '—'
                        )
                      ) : view === 'failed' ? (
                        <span className="text-red-600">{r.error ?? '—'}</span>
                      ) : (
                        r.flags.join(' · ')
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
};

/** What a HubSpot deal's "Salesforce history" link opens: the mirrored opportunity and its activity. */
export const OpportunityMirror: React.FC<{ id: string }> = ({ id }) => {
  const { data, isPending, error } = useQuery({
    queryKey: ['opsHubspot', 'import', 'opportunity', id],
    queryFn: () => getHubspotImportOpportunity(id),
  });
  if (isPending) return <LoadingSpinner />;
  if (error || !data)
    return (
      <Card title="Salesforce opportunity" className="mb-6">
        <EmptyState title={`No opportunity ${id} in the mirror`} />
      </Card>
    );
  const o = data.opportunity;
  const field = (label: string, v: unknown) =>
    v == null || v === '' ? null : (
      <div key={label}>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
          {label}
        </div>
        <div className="whitespace-pre-wrap text-sm text-gray-five">
          {String(v)}
        </div>
      </div>
    );
  return (
    <Card
      title={`Salesforce opportunity · ${o.Name ?? id}`}
      className="mb-6"
      right={
        <a className="text-xs text-blue-primary hover:underline" href="?">
          back to HubSpot
        </a>
      }
    >
      <div className="mb-3 text-xs text-gray-bold">
        Read-only mirror of the Salesforce record. Activity stays here; the
        HubSpot deal links to this page.
        {data.importRow?.hsDealId && (
          <span className="ml-2">HubSpot deal {data.importRow.hsDealId}.</span>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {field('Account', data.accountName)}
        {field(
          'Stage',
          `${o.StageName ?? ''}${
            o.IsClosed ? (o.IsWon ? ' (won)' : ' (lost)') : ''
          }`,
        )}
        {field('Amount', o.Amount != null ? money(Number(o.Amount)) : null)}
        {field('Close date', o.CloseDate)}
        {field(
          'Owner',
          data.owner ? `${data.owner.name} · ${data.owner.email}` : null,
        )}
        {field(
          'Contact',
          data.contact
            ? `${data.contact.name} · ${data.contact.email ?? ''}`
            : null,
        )}
        {field('Type', o.Type)}
        {field('Opportunity type', o.Opportunity_Type__c)}
        {field('Sub-type', o.Opportunity_Sub_Type__c)}
        {field('Students', o.Contract_No_of_Students__c)}
        {field('Next step', o.NextStep)}
        {field(
          'Created',
          o.CreatedDate ? String(o.CreatedDate).slice(0, 10) : null,
        )}
      </div>
      {field('Description', o.Description)}
      {field('Logistics summary', o.Logistics_Summary__c)}
      <div className="mt-4">
        <div className="mb-1 text-xs font-semibold text-gray-three">
          Activity · {data.tasks.length} tasks · {data.events.length} events
          {!data.activityMirrored && (
            <span className="ml-2 font-normal text-amber-700">
              tasks and events are not mirrored yet — they arrive with the next
              Sunday full export
            </span>
          )}
        </div>
        {data.tasks.length + data.events.length === 0 ? (
          <div className="text-xs text-gray-bold">
            No activity on this record.
          </div>
        ) : (
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr>
                <th className={thClass}>When</th>
                <th className={thClass}>Kind</th>
                <th className={thClass}>Subject</th>
                <th className={thClass}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {[
                ...data.events.map((e) => ({
                  at: e.StartDateTime ?? null,
                  kind: 'event',
                  subject: e.Subject,
                  notes: e.Description,
                })),
                ...data.tasks.map((t) => ({
                  at: t.ActivityDate ?? t.CreatedDate ?? null,
                  kind: t.TaskSubtype ?? 'task',
                  subject: t.Subject,
                  notes: t.Description,
                })),
              ]
                .sort((a, b) =>
                  String(b.at ?? '').localeCompare(String(a.at ?? '')),
                )
                .map((a, i) => (
                  <tr key={i}>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {a.at ? String(a.at).slice(0, 10) : '—'}
                    </td>
                    <td className={tdClass}>{a.kind}</td>
                    <td className={tdClass}>{a.subject ?? '—'}</td>
                    <td
                      className={`${tdClass} max-w-md whitespace-pre-wrap text-xs text-gray-bold`}
                    >
                      {a.notes ? String(a.notes).slice(0, 600) : ''}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
};
